import { Formik, Form as FormikForm } from 'formik';
import { isString } from 'lodash';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { Box, Flex } from '../grid';
import { Button } from '../button/new';
import { P, Subheading } from '../typography/new';
import { ScrollToFormError } from '../scroll-to-form-error';

import { Image } from '../image';

const HeadingComponent = ({ heading, paragraph }) => {
  if (!heading) {
    return null;
  }

  if (isString(heading)) {
    return (
      <Subheading variant="standard" mb={paragraph ? '8px' : 2} px={{ _: '24px', md: 2 }}>
        {heading}
      </Subheading>
    );
  }

  return heading;
};
HeadingComponent.propTypes = {
  heading: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  paragraph: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};
HeadingComponent.defaultProps = { heading: undefined, paragraph: null };

const ParagraphComponent = ({ paragraph }) => {
  if (!paragraph) {
    return null;
  }

  if (isString(paragraph)) {
    return (
      <P variant="medium" mb="32px">
        {paragraph}
      </P>
    );
  }

  return paragraph;
};
ParagraphComponent.propTypes = { paragraph: PropTypes.oneOfType([PropTypes.node, PropTypes.string]) };
ParagraphComponent.defaultProps = { paragraph: null };

const StyledButtonWrapper = styled(Box)``;

const Form = ({
  buttonAnalytics,
  buttonText,
  children,
  disabled,
  error,
  heading,
  initialValues,
  onSubmit,
  paragraph,
  submitted,
  validationSchema,
  bottomElement,
  topPadding,
  prefilled,
  formBackgroundColor,
  noXPadding,
  isFullWidthButton,
  isCheckout,
  hasStyles,
}) => (
  <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
    {({ dirty, handleSubmit, isSubmitting }) => (
      <StyledForm>
        <ScrollToFormError />
        <Flex
          flexDirection="column"
          bg={isCheckout ? 'transparent' : formBackgroundColor}
          pt={topPadding}
          width="100%"
          borderRadius={hasStyles && '32px'}
          overflow="hidden"
        >
          <HeadingComponent heading={heading} paragraph={paragraph} />
          <Box px={!noXPadding && { _: '24px', md: '32px' }}>
            <ParagraphComponent paragraph={paragraph} />
            {children}
            {error && (
              <Box mb="1">
                <StyledError variant={2}>{error}</StyledError>
              </Box>
            )}
            <StyledButtonWrapper>
              <Button
                analytics={buttonAnalytics}
                disabled={disabled || isSubmitting || (!dirty && !prefilled) || submitted}
                isLoading={isSubmitting || undefined}
                onClick={handleSubmit}
                type="submit"
                widthType={isFullWidthButton ? 'full' : 'fit'}
                size="tall"
              >
                {buttonText}
              </Button>
            </StyledButtonWrapper>
            {bottomElement}
          </Box>
          {!isCheckout && hasStyles && (
            <Image
              marginTop={{ sm: '24px', lg: '20px' }}
              userSelect="none"
              alt=""
              width="100%"
              src="/static/images/decal/bottom-decal-modal.svg"
            />
          )}
        </Flex>
      </StyledForm>
    )}
  </Formik>
);

const StyledForm = styled(FormikForm)`
  width: 100%;
`;

const StyledError = styled(P)`
  text-align: center;
  color: ${({ theme }) => theme.colors.errorPrimary};
`;

Form.propTypes = {
  buttonAnalytics: PropTypes.shape({}),
  buttonText: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  heading: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  initialValues: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func.isRequired,
  paragraph: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  submitted: PropTypes.bool,
  validationSchema: PropTypes.shape({}).isRequired,
  bottomElement: PropTypes.node,
  topPadding: PropTypes.number,
  prefilled: PropTypes.bool,
  formBackgroundColor: PropTypes.string,
  noXPadding: PropTypes.bool,
  isFullWidthButton: PropTypes.bool,
  isCheckout: PropTypes.bool,
  hasStyles: PropTypes.bool,
};

Form.defaultProps = {
  buttonAnalytics: undefined,
  disabled: false,
  error: null,
  heading: undefined,
  paragraph: null,
  submitted: false,
  bottomElement: null,
  topPadding: 2,
  prefilled: false,
  formBackgroundColor: 'white',
  noXPadding: false,
  isFullWidthButton: true,
  isCheckout: false,
  hasStyles: true,
};

const ComposedForm = observer(Form);
export { ComposedForm as Form };
